import { Injectable } from '@angular/core';
import {
  AddDocumentSnapshotListenerCallback,
  CallbackId,
  DocumentData,
  FirebaseFirestore,
  GetCollectionResult,
  GetDocumentResult,
} from '@capacitor-firebase/firestore';

/**
 * Thin injectable pass-through over the `@capacitor-firebase/firestore` plugin.
 *
 * The plugin only exposes static methods, which can't be spied/mocked in Karma. This
 * wrapper exists solely so consumers (e.g. the sync engine) can inject it and be
 * unit-tested against a spy. No business logic belongs here — each method is a direct
 * delegation to the same-named `FirebaseFirestore` static.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreApiService {
  getDocument<T extends DocumentData = DocumentData>(
    reference: string,
  ): Promise<GetDocumentResult<T>> {
    return FirebaseFirestore.getDocument<T>({ reference });
  }

  setDocument(reference: string, data: object, merge: boolean): Promise<void> {
    return FirebaseFirestore.setDocument({ reference, data, merge });
  }

  deleteDocument(reference: string): Promise<void> {
    return FirebaseFirestore.deleteDocument({ reference });
  }

  getCollection<T extends DocumentData = DocumentData>(
    reference: string,
  ): Promise<GetCollectionResult<T>> {
    return FirebaseFirestore.getCollection<T>({ reference });
  }

  addDocumentSnapshotListener<T extends DocumentData = DocumentData>(
    reference: string,
    callback: AddDocumentSnapshotListenerCallback<T>,
  ): Promise<CallbackId> {
    return FirebaseFirestore.addDocumentSnapshotListener<T>({ reference }, callback);
  }

  removeSnapshotListener(callbackId: string): Promise<void> {
    return FirebaseFirestore.removeSnapshotListener({ callbackId });
  }
}
